// The public share page (DS WatchPage, SD35: light, brand header, no nav rail — the recipient's
// view of a recording). §4 #29: GET /share/{token} resolves the token to a grackle and re-runs
// authorize_view with it — an invalid or revoked token is a 404 indistinguishable from "never
// existed" (§4.10/AC3), same NotFound page as WatchPage. FR-EN7: nothing about the visitor is
// recorded, and there is no "reply with a video". F3: a revoked/expired/exhausted token is 410
// (LinkUnavailable) and a gated link missing a factor is 401 share_locked (ShareGate); state
// lives in useShareWatch.
import { useParams } from "react-router-dom";
import { Button, Callout } from "@grackle/ui";
import { useSession } from "../session";
import NotFound from "../pages/NotFound";
import LinkUnavailable from "../pages/LinkUnavailable";
import { PageLoader } from "../components/ui";
import NotesPanel from "./NotesPanel";
import ShareGate from "./ShareGate";
import { useShareWatch } from "./useShareWatch";
import WatchView, { WatchPublicHeader } from "./WatchView";
import "./watch.css";

export default function SharePage() {
  const { token } = useParams();
  const { user } = useSession();
  // F3: load, gate/gone state machine and poll loop are shared with EmbedPage.
  const { watch, transcript, notFound, gone, gate, loadError, unlockToken, reload, onUnlocked } = useShareWatch(token);

  if (notFound) {
    return <NotFound />;
  }
  if (gone) {
    return <LinkUnavailable />;
  }

  const topbar = <WatchPublicHeader owner={watch?.grackle.owner_name} signedIn={Boolean(user)} />;

  if (loadError || gate || !watch) {
    return (
      <div className="watch watch--public">
        {topbar}
        <div className="watch--center">
          {loadError ? (
            <>
              <Callout tone="danger" icon="warning-circle">
                {loadError}
              </Callout>
              <Button variant="secondary" size="sm" onClick={reload}>
                Retry
              </Button>
            </>
          ) : gate && token ? (
            <ShareGate token={token} gate={gate} unlockToken={unlockToken} onUnlocked={onUnlocked} />
          ) : (
            <PageLoader label="Loading recording…" />
          )}
        </div>
      </div>
    );
  }

  return (
    <WatchView
      watch={watch}
      onReloadWatch={reload}
      transcript={transcript}
      userId={user?.id}
      variant="public"
      topbar={topbar}
      // F1: the summary card (DS WatchPage); every line carries its source timestamp (FR-M7).
      // The share token keeps link viewers authorized on GET /grackles/{id}/notes.
      notes={
        token && watch.grackle.status === "ready"
          ? (seek) => <NotesPanel grackleId={watch.grackle.id} share={token} canEdit={watch.can_edit} onSeek={seek} />
          : undefined
      }
    />
  );
}
